<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import FormField from '@/components/FormField.vue'
import PhoneIcon from '@/svg/phone.svg'
import AliyunCaptcha from '@/components/AliyunCaptcha.vue'
import { bindPhoneApi, sendSms, SmsPurpose } from '@/api'
import { StatusCode } from '@/enums/api'
import { useAppConfigStore } from '@/stores/appConfig'
import { useAssetsStore } from '@/stores/assets'
import { toastify } from '@/utils'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  completed: []
}>()

const appConfigStore = useAppConfigStore()
const assetsStore = useAssetsStore()

const phone = ref('')
const smsCode = ref('')
const phoneError = ref('')
const submitting = ref(false)
const sendingSms = ref(false)
const countdown = ref(0)
const captchaKey = ref(0)

const smsCaptchaRef = ref<InstanceType<typeof AliyunCaptcha> | null>(null)
const captchaConf = computed(() => appConfigStore.config?.ali_captcha_conf ?? null)

let countdownTimer: ReturnType<typeof setInterval> | null = null

const visible = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value),
})

const isPhoneValid = computed(() => /^1\d{10}$/.test(phone.value))

const canSendSms = computed(() => isPhoneValid.value && countdown.value === 0 && !sendingSms.value)

const canSubmit = computed(
  () => isPhoneValid.value && smsCode.value.length === 6 && !submitting.value,
)

const smsButtonText = computed(() => {
  if (countdown.value > 0) return `${countdown.value}s`
  return '获取'
})

watch(phone, (value) => {
  if (!value) {
    phoneError.value = ''
    return
  }
  phoneError.value = isPhoneValid.value ? '' : '手机号格式错误，请重新输入'
})

watch(visible, (value) => {
  if (!value) resetForm()
})

function clearCountdownTimer() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function startCountdown(seconds = 60) {
  clearCountdownTimer()
  countdown.value = seconds
  countdownTimer = setInterval(() => {
    if (countdown.value <= 1) {
      countdown.value = 0
      clearCountdownTimer()
    } else {
      countdown.value -= 1
    }
  }, 1000)
}

function resetForm() {
  phone.value = ''
  smsCode.value = ''
  phoneError.value = ''
  submitting.value = false
  sendingSms.value = false
  countdown.value = 0
  captchaKey.value++
  clearCountdownTimer()
}

function handleGetCodeClick() {
  if (!canSendSms.value) return
  if (!captchaConf.value) {
    toastify({ type: 'danger', message: '验证码配置未加载，请稍后重试' })
    return
  }
  smsCaptchaRef.value?.show()
}

async function onSmsCaptchaSuccess(captchaParam: string) {
  sendingSms.value = true
  try {
    await sendSms({
      number: phone.value,
      purpose: SmsPurpose.BindPhone,
      area: '86',
      captcha_param: captchaParam,
    })
    toastify({ type: 'success', message: '验证码已发送' })
    startCountdown(60)
  } catch (error: unknown) {
    const message = (error as Error).message || '发送验证码失败'
    toastify({ type: 'danger', message })
  } finally {
    sendingSms.value = false
    captchaKey.value++
  }
}

function onSmsCaptchaError(errorInfo: { code: string; msg: string }) {
  console.error('验证码加载失败', errorInfo)
  toastify({ type: 'danger', message: '验证码加载失败，请重试' })
}

const handleSubmit = async () => {
  if (!canSubmit.value) return

  submitting.value = true
  try {
    await bindPhoneApi({
      area_code: '86',
      phone: phone.value,
      sms_code: smsCode.value,
    })
    await assetsStore.refreshProfile()
    toastify({ type: 'success', message: '绑定成功' })
    emit('completed')
  } catch (error: unknown) {
    const err = error as { code?: number; message?: string }
    if (err.code === StatusCode.CodeExpired) {
      toastify({ type: 'danger', message: '验证码已过期，请重新获取' })
      return
    }
    toastify({ type: 'danger', message: err.message || '绑定失败' })
  } finally {
    submitting.value = false
  }
}

onUnmounted(() => {
  clearCountdownTimer()
})
</script>

<template>
  <van-popup
    v-model:show="visible"
    position="bottom"
    round
    safe-area-inset-bottom
    closeable
    :close-on-click-overlay="false"
    class="bind-phone-popup"
  >
    <div class="flex flex-col">
      <div class="relative flex items-center justify-center h-12 border-b border-border-light">
        <h3 class="text-base font-semibold text-text-primary m-0">绑定手机</h3>
      </div>

      <div class="px-4 pt-4 pb-2">
        <div class="flex items-center gap-2 mb-4">
          <PhoneIcon class="size-10 shrink-0" />
          <p class="text-sm font-semibold text-text-primary leading-relaxed m-0">
            为了您的资金安全，建议您绑定手机号码
          </p>
        </div>

        <van-cell-group :border="false">
          <form-field
            v-model="phone"
            name="phone"
            label="+86"
            label-width="36"
            placeholder="请输入手机号"
            maxlength="11"
            type="tel"
            clearable
          />
          <p v-if="phoneError" class="field-external-error" role="alert">
            {{ phoneError }}
          </p>

          <form-field
            v-model="smsCode"
            name="smsCode"
            class="mt-4"
            label-width="0"
            placeholder="请输入验证码"
            maxlength="6"
            type="number"
            clearable
          >
            <template #button>
              <button
                type="button"
                class="sms-get-btn"
                :class="{ 'sms-get-btn--active': canSendSms }"
                :disabled="!canSendSms"
                @click.stop="handleGetCodeClick"
              >
                {{ smsButtonText }}
              </button>
            </template>
          </form-field>
        </van-cell-group>

        <AliyunCaptcha
          v-if="captchaConf"
          ref="smsCaptchaRef"
          :key="captchaKey"
          :prefix="captchaConf.prefix"
          :scene-id="captchaConf.scene_id"
          @success="onSmsCaptchaSuccess"
          @error="onSmsCaptchaError"
        />
        <div class="mt-6">
          <van-button
            type="primary"
            size="large"
            block
            :loading="submitting"
            :disabled="!canSubmit"
            @click="handleSubmit"
          >
            确认
          </van-button>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<style scoped>
.field-external-error {
  margin: 6px 0 0;
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--van-danger-color);
}

.sms-get-btn {
  border: none;
  background: none;
  padding: 0 4px;
  font-size: 14px;
  line-height: 1;
  color: var(--color-text-third);
  cursor: not-allowed;
}

.sms-get-btn--active {
  color: var(--color-primary);
  cursor: pointer;
}

.sms-get-btn:disabled {
  cursor: not-allowed;
}
</style>
